// TrustHistoryView — Marketplace 2.0 forensic trust history.
// Spec: Playbook § 5.5 (Decision 5 — replace-in-place, anomaly stripes, italic-red "never").
// Demo: marketplace_demo.jsx TrustHistoryView (~lines 810–1132).
//
// Inner-content component only — the modal frame is owned by ListingDetailModal
// per the replace-in-place mechanic. This view fills the modal panel.
//
// Phase 2E.1 Step 7: data now fetches from the marketplace-trust-history edge
// function (server-side counterparty anonymization per Playbook § 5.5 lock).
// The `transactions` prop is preserved as an optional override for tests/
// storybook scenarios.

import React, { useState, useEffect } from 'react';
import TrustBadge from './TrustBadge';
import { supabase } from '../supabaseClient';

export default function TrustHistoryView({
  seller = {},
  initialTab = 'seller',
  onBack,
  onClose,
  transactions, // optional override; bypasses the edge function fetch when provided
  showToast,
}) {
  const [tab, setTab] = useState(initialTab === 'buyer' ? 'buyer' : 'seller');
  const [expanded, setExpanded] = useState(null);

  // Phase 2E.1 Step 7: fetched results from the edge function.
  // Shape: { seller: { count, rows }, buyer: { count, rows } }
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    // Skip the fetch when:
    //   - the parent supplied a transactions override (test/storybook), or
    //   - we don't know which user to query.
    if (transactions || !seller?.id) return;

    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    (async () => {
      const { data, error } = await supabase.functions.invoke('marketplace-trust-history', {
        body: { userId: seller.id, role: 'both' },
      });
      if (cancelled) return;
      if (error) {
        console.error('[TrustHistoryView] fetch failed:', error.message || error);
        setFetchError('Could not load trust history. Try closing and reopening.');
        setLoading(false);
        return;
      }
      setFetched(data || { seller: { count: 0, rows: [] }, buyer: { count: 0, rows: [] } });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [seller?.id, transactions]);

  const color = tab === 'seller' ? seller.sellerColor : seller.buyerColor;
  const count = tab === 'seller' ? seller.sellerCount : seller.buyerCount;
  const stats = seller.sellerStats || { clean: 0, unresolved: 0, flagged: 0 };
  const statsTotal = stats.clean + stats.unresolved + stats.flagged;

  // Pick the row source: prop override > fetched data > empty array.
  // Decision 5: chronological, newest first. Edge function already sorts DESC.
  const rolePool = transactions
    ? { seller: transactions, buyer: transactions.slice(0, Math.max(1, Math.min(3, transactions.length))) }
    : { seller: fetched?.seller?.rows || [], buyer: fetched?.buyer?.rows || [] };
  const list = tab === 'seller' ? rolePool.seller : rolePool.buyer;

  const isSprout = color === 'sprout';

  return (
    <>
      <style>{THV_CSS}</style>

      {/* Sticky back/close header */}
      <div className="thv-header">
        <button
          type="button"
          onClick={onBack}
          className="thv-back"
          aria-label="Back to listing"
        >
          <span style={{ fontSize: 14 }}>←</span>
          <span>Back to listing</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 22,
            color: 'var(--muted-5)',
            cursor: 'pointer',
            padding: 4,
            lineHeight: 1,
            minWidth: 44,
            minHeight: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >×</button>
      </div>

      <div style={{ padding: 18 }}>
        {/* Handle */}
        <div style={{
          fontSize: 14,
          color: 'var(--violet)',
          fontWeight: 500,
          fontFamily: "'JetBrains Mono', monospace",
          marginBottom: 12,
        }}>{seller.handle || '@collector'}</div>

        {isSprout ? (
          <SproutEmptyState
            seller={seller}
            tab={tab}
            onTab={setTab}
          />
        ) : (
          <>
            {/* lg badge for the current tab */}
            <div style={{ marginBottom: 14 }}>
              {color ? (
                <TrustBadge
                  color={color}
                  count={count}
                  role={tab}
                  size="lg"
                  interactive={false}
                />
              ) : (
                <TrustBadge color="loading" role={tab} size="lg" interactive={false} />
              )}
            </div>

            {/* Tabs */}
            <Tabs
              tab={tab}
              onTab={setTab}
              sellerCount={seller.sellerCount || 0}
              buyerCount={seller.buyerCount || 0}
            />

            {/* Stats breakdown — only on seller tab when we have stats */}
            {tab === 'seller' && statsTotal > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 6,
                marginBottom: 14,
              }}>
                <StatCell label="clean" value={stats.clean} pct={pct(stats.clean, statsTotal)} color="green" />
                <StatCell label="unresolved" value={stats.unresolved} pct={pct(stats.unresolved, statsTotal)} color="yellow" />
                <StatCell label="flagged" value={stats.flagged} pct={pct(stats.flagged, statsTotal)} color="red" />
              </div>
            )}

            <div style={{
              fontSize: 10,
              color: '#5a6580',
              marginBottom: 12,
              lineHeight: 1.5,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              Color reflects last 12 months. Count is lifetime.
            </div>

            {/* Transaction list */}
            <div style={{
              fontSize: 10,
              color: 'var(--muted-5)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 8,
              fontFamily: "'Share Tech Mono', monospace",
            }}>Recent transactions</div>

            {loading ? (
              <SkeletonRows />
            ) : fetchError ? (
              <div style={{
                fontSize: 12,
                color: 'var(--err)',
                padding: '24px 8px',
                textAlign: 'center',
                lineHeight: 1.5,
                background: 'rgba(var(--err-rgb),0.06)',
                border: '0.5px solid rgba(var(--err-rgb),0.3)',
                borderRadius: 4,
              }}>
                {fetchError}
              </div>
            ) : list.length === 0 ? (
              <div style={{
                fontSize: 11,
                color: 'var(--muted-6)',
                padding: '24px 8px',
                textAlign: 'center',
              }}>
                No {tab} transactions yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {list.map((txn, i) => (
                  <TransactionRow
                    key={txn.id || i}
                    txn={txn}
                    counterpartyRole={tab === 'seller' ? 'Buyer' : 'Seller'}
                    expanded={expanded === i}
                    onToggle={() => setExpanded(expanded === i ? null : i)}
                    onAnonHint={() => showToast && showToast('Counterparty handles are hidden when either party has privacy on. Adjust on your profile.')}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────

function SkeletonRows({ count = 4 }) {
  // Matches the row footprint so the layout doesn't jump when real data arrives.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            background: 'rgba(var(--wash-rgb),0.03)',
            borderRadius: 3,
            padding: '12px 10px',
            borderLeft: '2px solid transparent',
            height: 56,
            animation: 'tb-pulse 1.5s ease-in-out infinite',
            opacity: 0.6 + (i % 2) * 0.1,
          }}
        />
      ))}
    </div>
  );
}

function Tabs({ tab, onTab, sellerCount, buyerCount }) {
  return (
    <div style={{
      display: 'flex',
      gap: 0,
      borderBottom: '0.5px solid rgba(var(--wash-rgb),0.06)',
      marginBottom: 14,
    }}>
      <TabButton active={tab === 'seller'} onClick={() => onTab('seller')}>
        Seller history ({sellerCount})
      </TabButton>
      <TabButton active={tab === 'buyer'} onClick={() => onTab('buyer')}>
        Buyer history ({buyerCount})
      </TabButton>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      style={{
        padding: '8px 12px',
        fontSize: 12,
        color: active ? 'var(--ok)' : 'var(--muted-5)',
        fontWeight: active ? 500 : 400,
        borderBottom: active ? '2px solid var(--ok-2)' : '2px solid transparent',
        marginBottom: -0.5,
        cursor: 'pointer',
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        borderBottomStyle: 'solid',
        borderBottomWidth: 2,
        borderBottomColor: active ? 'var(--ok-2)' : 'transparent',
        fontFamily: "'JetBrains Mono', monospace",
        minHeight: 44,
      }}
    >
      {children}
    </button>
  );
}

function StatCell({ label, value, pct, color }) {
  const bg = { green: 'rgba(var(--ok-2-rgb),0.1)', yellow: 'rgba(var(--amber-rgb),0.1)', red: 'rgba(var(--err-rgb),0.1)' }[color];
  const fg = { green: 'var(--ok)', yellow: 'var(--warn)', red: 'var(--err)' }[color];
  return (
    <div style={{ textAlign: 'center', padding: '8px 4px', background: bg, borderRadius: 3 }}>
      <div style={{
        color: fg,
        fontWeight: 500,
        fontSize: 16,
        fontFamily: "'JetBrains Mono', monospace",
      }}>{value}</div>
      <div style={{
        color: 'var(--muted-5)',
        fontSize: 9,
        fontFamily: "'Share Tech Mono', monospace",
      }}>{label} ({pct}%)</div>
    </div>
  );
}

function TransactionRow({ txn, counterpartyRole, expanded, onToggle, onAnonHint }) {
  const STATUS = {
    clean:      { bg: 'rgba(var(--ok-2-rgb),0.15)', fg: 'var(--ok)', icon: '✓', label: 'clean',      stripe: null },
    unresolved: { bg: 'rgba(var(--amber-rgb),0.15)', fg: 'var(--warn)', icon: '⚠', label: 'unresolved', stripe: 'rgba(var(--amber-rgb),0.5)' },
    flagged:    { bg: 'rgba(var(--err-rgb),0.15)', fg: 'var(--err)', icon: '✕', label: 'flagged',    stripe: 'rgba(var(--err-rgb),0.5)' },
  };
  const s = STATUS[txn.status] || STATUS.clean;
  const hasTimeline = !!txn.timeline;

  return (
    <div
      onClick={hasTimeline ? onToggle : undefined}
      role={hasTimeline ? 'button' : undefined}
      tabIndex={hasTimeline ? 0 : undefined}
      onKeyDown={hasTimeline ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle && onToggle(); }
      } : undefined}
      aria-expanded={hasTimeline ? expanded : undefined}
      style={{
        background: expanded ? 'rgba(var(--wash-rgb),0.04)' : 'rgba(var(--wash-rgb),0.03)',
        borderRadius: 3,
        padding: '8px 10px',
        borderLeft: s.stripe ? `2px solid ${s.stripe}` : '2px solid transparent',
        cursor: hasTimeline ? 'pointer' : 'default',
        transition: 'background 0.15s ease',
      }}
    >
      {/* Row top — status pill, price, date */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 3,
        gap: 8,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 9,
            padding: '1px 6px',
            background: s.bg,
            color: s.fg,
            borderRadius: 2,
            fontWeight: 500,
            fontFamily: "'JetBrains Mono', monospace",
          }}>{s.icon} {s.label}</span>
          <span style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--text-bright)',
            fontFamily: "'JetBrains Mono', monospace",
          }}>${txn.price}</span>
        </div>
        <span style={{
          fontSize: 10,
          color: 'var(--muted-5)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{txn.date}</span>
      </div>

      {/* Item */}
      <div style={{
        fontSize: 10,
        color: 'var(--muted-5)',
        marginBottom: 2,
        fontFamily: "'JetBrains Mono', monospace",
      }}>{txn.item}</div>

      {/* Escrow-skipped marker — neutral fact (Marketplace 2.1): both parties
          mutually opted out of escrow on this $500+ deal. */}
      {txn.escrowSkipped && (
        <div style={{
          display: 'inline-block',
          fontSize: 8,
          letterSpacing: '0.04em',
          color: 'var(--muted-5)',
          border: '0.5px solid rgba(var(--muted-5-rgb),0.4)',
          borderRadius: 3,
          padding: '1px 5px',
          marginBottom: 4,
          fontFamily: "'JetBrains Mono', monospace",
        }}>Escrow skipped · paid via PayPal</div>
      )}

      {/* Counterparty */}
      <div style={{
        fontSize: 9,
        color: '#5a6580',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {txn.anon ? (
          <>
            to CK collector ·{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onAnonHint && onAnonHint(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onAnonHint && onAnonHint(); }
              }}
              style={{
                color: 'var(--violet)',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                cursor: 'pointer',
              }}
            >{counterpartyRole} stayed anonymous</span>
          </>
        ) : (
          <>to <span style={{ color: 'var(--violet)' }}>{txn.partner}</span></>
        )}
      </div>

      {/* Expanded forensic timeline */}
      {expanded && hasTimeline && (
        <div style={{
          marginTop: 10,
          padding: 10,
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 3,
          borderTop: '0.5px solid rgba(var(--wash-rgb),0.06)',
        }}>
          <div style={{
            fontSize: 9,
            color: 'var(--muted-5)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 6,
            fontFamily: "'Share Tech Mono', monospace",
          }}>Timeline</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 10 }}>
            <TimelineRow label="Payment sent" value={txn.timeline.paid} />
            <TimelineRow label="Shipped" value={txn.timeline.shipped} />
            <TimelineRow label="Delivered" value={txn.timeline.delivered} />
            <TimelineRow
              label="Buyer confirmed"
              value={txn.timeline.buyerConfirmed}
              never={txn.timeline.buyerConfirmed === 'never'}
            />
            <TimelineRow
              label="Seller confirmed"
              value={txn.timeline.sellerConfirmed}
              never={txn.timeline.sellerConfirmed === 'never'}
            />
          </div>
          {txn.timeline.result && (
            <div style={{
              fontSize: 10,
              color: s.fg,
              lineHeight: 1.5,
              marginTop: 8,
              paddingTop: 8,
              borderTop: '0.5px solid rgba(var(--wash-rgb),0.08)',
            }}>{txn.timeline.result}</div>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineRow({ label, value, never }) {
  // Decision 5: unconfirmed actions render as italic red "never".
  // The single most important typographic moment in the product.
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--muted-5)' }}>{label}</span>
      <span style={{
        color: never ? 'var(--err)' : 'var(--text-bright)',
        fontStyle: never ? 'italic' : 'normal',
        fontFamily: "'JetBrains Mono', monospace",
      }}>{value}</span>
    </div>
  );
}

function SproutEmptyState({ seller, tab, onTab }) {
  const sellerCount = seller.sellerCount || 0;
  const buyerCount = seller.buyerCount || 0;
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <TrustBadge color="sprout" role={tab} size="lg" interactive={false} />
      </div>
      <Tabs tab={tab} onTab={onTab} sellerCount={sellerCount} buyerCount={buyerCount} />
      <div style={{
        background: 'rgba(var(--ok-2-rgb),0.04)',
        borderRadius: 6,
        padding: '24px 16px',
        textAlign: 'center',
        marginBottom: 14,
      }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>🌱</div>
        <div style={{
          fontSize: 13,
          color: 'var(--text-bright)',
          fontWeight: 500,
          marginBottom: 6,
        }}>No transactions yet</div>
        <div style={{
          fontSize: 11,
          color: 'var(--muted-5)',
          lineHeight: 1.5,
        }}>
          {seller.handle || 'This collector'} hasn't completed a transaction as a {tab} yet.
          Their history will appear here after their first {tab === 'seller' ? 'sale' : 'purchase'}.
        </div>
      </div>
      {seller.verified && (
        <div style={{
          background: 'rgba(var(--ok-2-rgb),0.06)',
          border: '0.5px solid rgba(var(--ok-2-rgb),0.3)',
          borderRadius: 6,
          padding: '10px 12px',
        }}>
          <div style={{
            fontSize: 11,
            padding: '2px 7px',
            background: 'rgba(var(--ok-2-rgb),0.2)',
            color: 'var(--ok)',
            borderRadius: 2,
            border: '0.5px solid rgba(var(--ok-2-rgb),0.4)',
            fontWeight: 500,
            display: 'inline-block',
            marginBottom: 4,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '0.04em',
          }}>✓ VERIFIED</div>
          <div style={{
            fontSize: 10,
            color: 'var(--muted-5)',
            lineHeight: 1.5,
          }}>
            {seller.handle || 'This collector'}'s identity has been verified by Persona. Real person, just new.
          </div>
        </div>
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────

function pct(n, total) {
  if (!total) return 0;
  return Math.round((n / total) * 100);
}

const THV_CSS = `
  .thv-header {
    position: sticky;
    top: 0;
    background: var(--panel-2);
    padding: 12px 18px;
    border-bottom: 0.5px solid rgba(var(--wash-rgb),0.06);
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 2;
  }
  .thv-back {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    padding: 4px 8px 4px 0;
    background: transparent;
    border: none;
    color: var(--violet);
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    min-height: 44px;
  }
  .thv-back:hover { color: var(--violet); }
`;
